import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/lib/shadcn/components/ui/select';
import { Label } from '@/lib/shadcn/components/ui/label';
import { cn } from '@/lib/utils';
import { useListTenantsQuery } from '@/features/tenants/queries/tenants.query';
import type { Tenant } from '@/features/tenants/models';

const ALL_VALUE = '__all__';

export interface TenantSelectProps {
  value: string | null;
  onChange: (tenantId: string | null) => void;
  disabled?: boolean;
  /** Applied to the outer wrapper (label + select row). */
  className?: string;
  id?: string;
  /** Hide the visible "Tenant" title; still sets `id` / `htmlFor` when `id` is passed. */
  hideLabel?: boolean;
  /**
   * When false, hides the "All" option; the value must be a specific tenant (e.g. user creation).
   * @default true
   */
  includeAllOption?: boolean;
}

export const TenantSelect: React.FC<TenantSelectProps> = ({
  value,
  onChange,
  disabled,
  className,
  id,
  hideLabel = false,
  includeAllOption = true,
}) => {
  const { t } = useTranslation('shared');
  const generatedId = React.useId();
  const controlId = id ?? generatedId;
  const { data, isPending } = useListTenantsQuery({
    page: 1,
    limit: 1000,
  });

  const tenants: Tenant[] = data?.data?.tenants ?? [];
  /** Empty while loading so Radix shows `placeholder` (muted) on the trigger. */
  const selectValue = isPending
    ? ''
    : includeAllOption
      ? value ?? ALL_VALUE
      : value ?? '';

  return (
    <div
      className={cn(
        'flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-2',
        className
      )}
    >
      {!hideLabel && (
        <Label
          htmlFor={controlId}
          className="text-sm font-medium text-foreground whitespace-nowrap shrink-0"
        >
          {t('tenantFilter.label', 'Tenant')}
        </Label>
      )}
      <Select
        value={selectValue || undefined}
        onValueChange={v => onChange(v === ALL_VALUE ? null : v)}
        disabled={disabled || isPending}
      >
        <SelectTrigger id={controlId} className="w-[220px] min-w-[12rem]">
          <SelectValue
            placeholder={
              isPending
                ? t('tenantFilter.loading', 'Loading tenants…')
                : t('tenantFilter.placeholder', 'Select tenant')
            }
          />
        </SelectTrigger>
        <SelectContent>
          {includeAllOption && (
            <SelectItem value={ALL_VALUE}>
              {t('tenantFilter.all', 'All')}
            </SelectItem>
          )}
          {tenants.map(tenant => {
            const tid = tenant.TenantId?.trim();
            if (!tid) return null;
            return (
              <SelectItem key={tid} value={tid}>
                {tenant.TenantName?.trim() || tid}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
};

/** Tenant selector styled like other DataTable filters (label above control). */
export const TenantSelectFilterBlock: React.FC<TenantSelectProps> = props => {
  const { t } = useTranslation('shared');
  return (
    <div className="flex flex-col space-y-1 shrink-0">
      <span className="text-sm font-medium text-foreground">
        {t('tenantFilter.label', 'Tenant')}
      </span>
      <TenantSelect {...props} hideLabel className="gap-0" />
    </div>
  );
};
